import { Col, Input, Row, Table, Tabs } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import VuiPageTitle from "../../../components/page-title";
import { useTranslation } from "react-i18next";
import VuiButton from "../../../components/button";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table/interface";
import { CustomerList } from "./interface";
import VuiTableActionButton from "../../../components/table-action-button";
import { useGetCustomer } from "../hooks/use-get-customer";
import VuiAccessible from "../../../components/accessible";
import { IPaginationParams } from "../../../utils/interfaces/pagination-params.interface";
import { overrideTableSortOrder } from "../../../utils/helpers";
import useDebounce from "../../../hooks/use-debounce";
import { DEFAULT_PAGE_SIZE, LEAD_TABS } from "../../../constants";
import { LeadDataList } from "../../lead/list/interface";
import moment from "moment/moment";
import Text from "antd/lib/typography/Text";
import { useGetLead } from "../../lead/hooks/use-get-lead";

const qs = require("qs-lite");

const CustomerListModule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, loadData, isOnFetchingData, totalData } = useGetCustomer();
  const {
    data: leadData,
    loadData: loadLeadData,
    isOnFetchingData: isOnFetchingLeadData,
    totalData: totalLeadData,
  } = useGetLead();
  const [hasInitialize, setHasInitialize] = useState<boolean>(false);

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 250);
  const [selectedTab, setSelectedTab] = useState<LEAD_TABS>(LEAD_TABS.CLIENT);

  const [searchParams, setSearchParams] = useSearchParams();
  const [tableQuery, setTableQuery] = useState<IPaginationParams>({
    page: Number(searchParams.get("page")) || 1,
    per_page: Number(searchParams.get("perPage")) || DEFAULT_PAGE_SIZE,
    sorted_by: searchParams.get("sortedBy") || "ascend",
    order_by: (searchParams.get("orderBy") as string) || "name",
  });

  const [searchParamsLead, setSearchParamsLead] = useSearchParams();
  const [tableQueryLead, setTableQueryLead] = useState<IPaginationParams>({
    page: Number(searchParamsLead.get("page")) || 1,
    per_page: Number(searchParamsLead.get("perPage")) || DEFAULT_PAGE_SIZE,
    sorted_by: searchParamsLead.get("sortedBy"),
    order_by: searchParamsLead.get("orderBy") as string,
    with: ["customer.type"],
  });

  const handleChangeSearchParams = useCallback(async () => {
    const params = {} as IPaginationParams;

    Object.assign(params, {
      page: tableQuery.page,
    });

    Object.assign(params, {
      perPage: tableQuery.per_page,
    });

    if (debouncedSearch) {
      Object.assign(params, {
        search: debouncedSearch,
      });
    }

    if (tableQuery.order_by) {
      Object.assign(params, {
        orderBy: tableQuery.order_by,
      });
    }

    if (tableQuery.sorted_by) {
      Object.assign(params, {
        sortedBy: tableQuery.sorted_by,
      });
    }

    const queryParams = qs.stringify(params, { indices: false });

    if (queryParams) {
      setSearchParams(`?${queryParams}`);
    } else {
      navigate("/access");
    }

    const tableParams: IPaginationParams = {
      page: params.page,
      per_page: tableQuery.per_page,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(tableQuery.order_by ? { order_by: tableQuery.order_by } : {}),
      ...(tableQuery.sorted_by
        ? { sorted_by: overrideTableSortOrder(tableQuery.sorted_by) }
        : {}),
    };

    await loadData(tableParams);

    setHasInitialize(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableQuery,
    debouncedSearch,
    loadData,
    navigate,
    searchParams,
    setSearchParams,
  ]);

  const clientColumns: ColumnsType<CustomerList> = [
    {
      title: t("common.text.clientId"),
      dataIndex: "code",
      key: "code",
      sorter: true,
      defaultSortOrder: tableQuery.order_by === "code" && tableQuery.sorted_by,
    },
    {
      title: t("common.text.name"),
      dataIndex: "name",
      key: "name",
      sorter: true,
      defaultSortOrder: tableQuery.order_by === "name" && tableQuery.sorted_by,
    },
    {
      title: t("common.text.alias"),
      dataIndex: "alias_name",
      key: "alias_name",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "alias_name" && tableQuery.sorted_by,
    },
    {
      title: t("common.text.action"),
      key: "id",
      className: "table-action",
      fixed: "right",
      width: 110,
      render: (text, record) => {
        return (
          <VuiTableActionButton
            detailLink={`/client/${record.id}`}
            detailPermissionName="customer.show"
          />
        );
      },
    },
  ];

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any, extra: any) => {
      const params: IPaginationParams = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      if (sorter.order) {
        Object.assign(params, {
          sorted_by: sorter.order,
          order_by: sorter.field,
        });
      } else {
        Object.assign(params, {
          sorted_by: null,
          order_by: null,
        });
      }

      setTableQuery(params);
    },
    []
  );
  const handleChangeSearchParamsLead = useCallback(async () => {
    const params = {} as IPaginationParams;

    Object.assign(params, {
      page: tableQueryLead.page,
    });

    Object.assign(params, {
      perPage: tableQueryLead.per_page,
    });

    if (debouncedSearch) {
      Object.assign(params, {
        search: debouncedSearch,
      });
    }

    if (tableQueryLead.order_by) {
      Object.assign(params, {
        orderBy: tableQueryLead.order_by,
      });
    }

    if (tableQueryLead.sorted_by) {
      Object.assign(params, {
        sortedBy: tableQueryLead.sorted_by,
      });
    }

    if (tableQueryLead.with) {
      Object.assign(params, {
        with: tableQueryLead.with,
      });
    }

    const queryParams = qs.stringify(params, { indices: false });

    if (queryParams) {
      setSearchParamsLead(`?${queryParams}`);
    } else {
      navigate("/access");
    }

    const tableParams: IPaginationParams = {
      page: params.page,
      per_page: tableQueryLead.per_page,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(tableQueryLead.order_by
        ? { order_by: tableQueryLead.order_by }
        : { order_by: "created_at" }),
      ...(tableQueryLead.sorted_by
        ? { sorted_by: overrideTableSortOrder(tableQueryLead.sorted_by) }
        : { sorted_by: "desc" }),
      with: tableQueryLead.with,
    };

    await loadLeadData(tableParams);

    setHasInitialize(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableQueryLead,
    loadLeadData,
    navigate,
    searchParamsLead,
    setSearchParamsLead,
  ]);

  const handleTableLeadChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      const params: IPaginationParams = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      if (sorter.order) {
        Object.assign(params, {
          sorted_by: sorter.order,
          order_by: sorter.field,
        });
      } else {
        Object.assign(params, {
          sorted_by: null,
          order_by: null,
        });
      }

      setTableQueryLead(params);
    },
    []
  );

  const leadsColumns: ColumnsType<LeadDataList> = [
    {
      title: t("common.text.date"),
      dataIndex: "date",
      key: "date",
      sorter: true,
      defaultSortOrder: tableQuery.order_by === "date" && tableQuery.sorted_by,
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: t("common.text.client"),
      dataIndex: "customer_name",
      key: "customer_name",
      sorter: true,
      render: (text, record) => {
        return (
          <Text>
            {record.customer?.type?.label
              ? record.customer.type.label + " "
              : ""}
            {text}
          </Text>
        );
      },
    },
    {
      title: t("common.text.action"),
      key: "id",
      className: "table-action",
      fixed: "right",
      width: 110,
      render: (text, record) => {
        return (
          <VuiTableActionButton
            detailLink={`/client/lead/${record.id}`}
            detailPermissionName="lead.show"
          />
        );
      },
    },
  ];

  useEffect(() => {
    (async () => {
      await handleChangeSearchParams();
      await handleChangeSearchParamsLead();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableQuery]);

  useEffect(() => {
    if (hasInitialize) {
      setTableQuery((prevState) => ({
        ...prevState,
        search: debouncedSearch,
        page: 1,
      }));
      setTableQueryLead((prevState) => ({
        ...prevState,
        page: 1,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <>
      <VuiPageTitle title={t("pages.client.list.title")}>
        {selectedTab === LEAD_TABS.CLIENT && (
          <VuiAccessible access="customer.store">
            <Link to="/client/add">
              <VuiButton
                title={t("common.button.addItem", {
                  item: t("common.text.client"),
                })}
                buttonProps={{
                  icon: <PlusOutlined />,
                  size: "middle",
                }}
              />
            </Link>
          </VuiAccessible>
        )}
      </VuiPageTitle>
      <Row gutter={[16, 16]} className="mb16">
        <Col xs={24} md={8}>
          <Input
            allowClear
            placeholder={t("common.form.search.placeholder")}
            prefix={<SearchOutlined />}
            size="middle"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
      </Row>

      <Tabs
        defaultActiveKey={LEAD_TABS.CLIENT}
        onChange={(key) => {
          setSelectedTab(key as LEAD_TABS);
          setSearch("");
        }}
      >
        <Tabs.TabPane tab={t("pages.client.list.title")} key={LEAD_TABS.CLIENT}>
          <Table
            size="small"
            columns={clientColumns}
            dataSource={data}
            loading={isOnFetchingData}
            onChange={handleTableChange}
            pagination={{
              total: totalData,
              current: tableQuery.page,
              pageSize: tableQuery.per_page,
              showSizeChanger: true,
            }}
            scroll={{ x: "max-content" }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab={t("pages.lead.list.title")} key={LEAD_TABS.LEAD}>
          <Table
            size="small"
            columns={leadsColumns}
            dataSource={leadData}
            loading={isOnFetchingLeadData}
            onChange={handleTableLeadChange}
            pagination={{
              current: tableQuery.page,
              showSizeChanger: true,
              pageSize: tableQuery.per_page,
              total: totalLeadData,
            }}
            scroll={{ x: "max-content" }}
          />
        </Tabs.TabPane>
      </Tabs>
    </>
  );
};

export default CustomerListModule;
